import { useEffect, useRef } from "react";
import { useTheme } from "@mui/material";
import { XMarkIcon } from "@heroicons/react/20/solid";
import { getSkillColors } from "../../helpers/color";
import { MAX_PANELIST_SKILLS } from "../../utils/panelists";

const appendSkills = (skills, names) => {
  const next = [...skills];
  names.forEach((name) => {
    if (next.length >= MAX_PANELIST_SKILLS) return;
    const skill = name.trim();
    if (!skill) return;
    if (next.some((item) => item.toLowerCase() === skill.toLowerCase())) return;
    next.push(skill);
  });
  return next;
};

const isDuplicateSkill = (skills, name) => {
  const skill = String(name || "").trim().toLowerCase();
  if (!skill) return false;
  return skills.some((item) => item.toLowerCase() === skill);
};

const commitDraft = (skills, draft, onSkillsChange, onSkillDraftChange) => {
  const skill = String(draft || "").trim();
  if (!skill || isDuplicateSkill(skills, skill)) return;
  const next = appendSkills(skills, [skill]);
  if (next.length !== skills.length) {
    onSkillsChange(next);
    onSkillDraftChange("");
  }
};

const PanelistProfileFields = ({
  idPrefix,
  role,
  onRoleChange,
  experience,
  onExperienceChange,
  skills,
  onSkillsChange,
  skillDraft,
  onSkillDraftChange,
  inputClassName,
  inputStyle,
  compact = false,
}) => {
  const theme = useTheme();
  const labelClass = compact ? "block text-xs font-medium mb-1" : "block text-sm font-medium mb-2";
  const labelStyle = { color: theme.palette.text.main };
  const fieldGap = compact ? "gap-2" : "gap-5";
  const atSkillLimit = skills.length >= MAX_PANELIST_SKILLS;
  const overSkillLimit = skills.length > MAX_PANELIST_SKILLS;
  const duplicateSkill = isDuplicateSkill(skills, skillDraft);
  const skillDescribedBy = [
    atSkillLimit ? `${idPrefix}-skill-limit` : "",
    duplicateSkill ? `${idPrefix}-skill-duplicate` : "",
  ].filter(Boolean).join(" ");
  const skillInputRef = useRef(null);
  const skillsRef = useRef(skills);
  const onSkillsChangeRef = useRef(onSkillsChange);
  const onSkillDraftChangeRef = useRef(onSkillDraftChange);
  skillsRef.current = skills;
  onSkillsChangeRef.current = onSkillsChange;
  onSkillDraftChangeRef.current = onSkillDraftChange;

  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.key !== "Enter") return;
      if (event.target !== skillInputRef.current) return;
      event.preventDefault();
      event.stopPropagation();
      commitDraft(
        skillsRef.current,
        event.target.value,
        onSkillsChangeRef.current,
        onSkillDraftChangeRef.current,
      );
    };
    document.addEventListener("keydown", onKeyDown, true);
    return () => document.removeEventListener("keydown", onKeyDown, true);
  }, []);

  return (
    <div className={`flex flex-col ${fieldGap}`}>
      <div>
        <label htmlFor={`${idPrefix}-role`} className={labelClass} style={labelStyle}>
          Designation
        </label>
        <input
          id={`${idPrefix}-role`}
          type="text"
          value={role}
          onChange={(e) => onRoleChange(e.target.value)}
          maxLength={100}
          placeholder="Enter designation"
          className={inputClassName}
          style={inputStyle}
        />
      </div>
      <div>
        <label htmlFor={`${idPrefix}-experience`} className={labelClass} style={labelStyle}>
          Experience (in years)
        </label>
        <input
          id={`${idPrefix}-experience`}
          type="text"
          inputMode="numeric"
          value={experience}
          onChange={(e) => {
            const value = e.target.value;
            if (!/^\d*$/.test(value)) return;
            if (value !== "" && Number(value) > 60) return;
            onExperienceChange(value);
          }}
          placeholder="Years"
          className={inputClassName}
          style={inputStyle}
        />
      </div>
      <div>
        <label htmlFor={`${idPrefix}-skill`} className={labelClass} style={labelStyle}>
          Skills
        </label>
        <input
          id={`${idPrefix}-skill`}
          ref={skillInputRef}
          type="text"
          value={skillDraft}
          onChange={(e) => onSkillDraftChange(e.target.value)}
          maxLength={100}
          disabled={atSkillLimit}
          placeholder={
            atSkillLimit
              ? `Maximum of ${MAX_PANELIST_SKILLS} skills`
              : "Type a skill and press Enter to add"
          }
          aria-describedby={skillDescribedBy || undefined}
          aria-invalid={duplicateSkill || undefined}
          className={`${inputClassName}${atSkillLimit ? " opacity-60 cursor-not-allowed" : ""}`}
          style={inputStyle}
        />
        {duplicateSkill && (
          <p id={`${idPrefix}-skill-duplicate`} className="mt-1 text-xs text-red-500">
            Skill already exists
          </p>
        )}
        {atSkillLimit && (
          <p
            id={`${idPrefix}-skill-limit`}
            className="mt-1 text-xs"
            style={{ color: overSkillLimit ? theme.palette.error.main : theme.palette.text.secondary }}
          >
            A panelist can have up to {MAX_PANELIST_SKILLS} skills.
          </p>
        )}
        {skills.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-2">
            {skills.map((skill) => {
              const { bgColor, borderColor } = getSkillColors(skill);
              return (
                <span
                  key={skill}
                  className="inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-xs"
                  style={{
                    backgroundColor: bgColor,
                    borderColor,
                    color: "rgba(0, 0, 0, 1)",
                  }}
                >
                  {skill}
                  <button
                    type="button"
                    aria-label={`Remove ${skill}`}
                    onClick={() => onSkillsChange(skills.filter((item) => item !== skill))}
                    className="inline-flex items-center"
                  >
                    <XMarkIcon className="h-3 w-3" />
                  </button>
                </span>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default PanelistProfileFields;
